import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, BarChart3, TrendingUp, Users, Eye, Heart,
  MessageCircle, Share2, Zap, Calendar, ArrowUpRight,
  ArrowDownRight, Flame, Target
} from 'lucide-react';
import { api } from '@/lib/api';

const DailyDropAnalytics = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get(`/daily-drop/analytics?range=${timeRange}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Use mock data
      setAnalytics({
        overview: {
          totalPosts: 12,
          totalReach: 4580,
          totalEngagement: 342,
          avgEngagementRate: 7.5,
          currentStreak: 5,
          bestStreak: 12
        },
        growth: {
          reachChange: 23.5,
          engagementChange: -5.2,
          followersChange: 8.1
        },
        topPosts: [
          { id: '1', caption: 'Morning routine that changed my life...', reach: 1240, likes: 89, comments: 23 },
          { id: '2', caption: 'The secret to productivity...', reach: 980, likes: 67, comments: 15 },
          { id: '3', caption: 'Weekend vibes only...', reach: 756, likes: 54, comments: 8 }
        ],
        platformBreakdown: [
          { platform: 'Instagram', posts: 8, reach: 2340, engagement: 189 },
          { platform: 'TikTok', posts: 6, reach: 1560, engagement: 98 },
          { platform: 'Twitter', posts: 4, reach: 680, engagement: 55 }
        ],
        weeklyActivity: [
          { day: 'Mon', posts: 2 },
          { day: 'Tue', posts: 1 },
          { day: 'Wed', posts: 2 },
          { day: 'Thu', posts: 1 },
          { day: 'Fri', posts: 3 },
          { day: 'Sat', posts: 2 },
          { day: 'Sun', posts: 1 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const timeRanges = [
    { id: '7d', label: '7 Days' },
    { id: '30d', label: '30 Days' },
    { id: '90d', label: '90 Days' },
  ];

  const StatCard = ({ title, value, subtitle, icon: Icon, color, change }) => (
    <Card className={`bg-gradient-to-br ${color} backdrop-blur-sm`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium opacity-80">{title}</p>
            <p className="text-3xl font-bold text-white">{value}</p>
            {subtitle && <p className="text-xs opacity-60">{subtitle}</p>}
            {change !== undefined && (
              <div className={`flex items-center gap-1 mt-1 text-sm ${change >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                {change >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {Math.abs(change)}%
              </div>
            )}
          </div>
          <div className="bg-white/10 p-3 rounded-full">
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <header className="border-b border-purple-500/20 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-400" />
                Analytics
              </h1>
              <p className="text-xs text-purple-300">Track your growth and engagement</p>
            </div>
          </div>
          <div className="flex gap-2">
            {timeRanges.map((range) => (
              <Button
                key={range.id}
                variant={timeRange === range.id ? 'default' : 'outline'}
                size="sm"
                className={timeRange === range.id 
                  ? 'bg-purple-600 hover:bg-purple-500' 
                  : 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20'}
                onClick={() => setTimeRange(range.id)}
                data-testid={`range-${range.id}`}
              >
                {range.label}
              </Button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Reach"
            value={analytics?.overview?.totalReach?.toLocaleString() || '0'}
            subtitle="impressions"
            icon={Eye}
            color="from-blue-500/20 to-cyan-500/20 border border-blue-500/30"
            change={analytics?.growth?.reachChange}
          />
          <StatCard
            title="Engagement"
            value={analytics?.overview?.totalEngagement?.toLocaleString() || '0'}
            subtitle="interactions"
            icon={Heart}
            color="from-pink-500/20 to-red-500/20 border border-pink-500/30"
            change={analytics?.growth?.engagementChange}
          />
          <StatCard
            title="Posts"
            value={analytics?.overview?.totalPosts || '0'}
            subtitle="this period"
            icon={Calendar}
            color="from-purple-500/20 to-violet-500/20 border border-purple-500/30"
          />
          <StatCard
            title="Current Streak"
            value={analytics?.overview?.currentStreak || '0'}
            subtitle={`Best: ${analytics?.overview?.bestStreak || 0} days`}
            icon={Flame}
            color="from-orange-500/20 to-amber-500/20 border border-orange-500/30"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Weekly Activity */}
          <Card className="lg:col-span-2 bg-black/30 border-purple-500/20 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-400" />
                Weekly Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between h-40 gap-2">
                {analytics?.weeklyActivity?.map((day, index) => (
                  <div key={day.day} className="flex-1 flex flex-col items-center gap-2">
                    <div 
                      className="w-full bg-gradient-to-t from-purple-600 to-pink-500 rounded-t-lg transition-all"
                      style={{ height: `${(day.posts / 3) * 100}%`, minHeight: day.posts > 0 ? '20px' : '4px' }}
                    />
                    <span className="text-purple-300/60 text-xs">{day.day}</span>
                  </div>
                ))}
              </div>
              <p className="text-center text-purple-300/50 text-sm mt-4">
                Posts per day (last 7 days)
              </p>
            </CardContent>
          </Card>

          {/* Engagement Rate */}
          <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Target className="h-5 w-5 text-green-400" />
                Engagement Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center py-8">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full border-8 border-purple-500/20 flex items-center justify-center">
                    <div className="text-center">
                      <span className="text-4xl font-bold text-white">{analytics?.overview?.avgEngagementRate || 0}</span>
                      <span className="text-purple-300">%</span>
                    </div>
                  </div>
                  <div 
                    className="absolute top-0 left-0 w-32 h-32 rounded-full border-8 border-transparent border-t-green-500 border-r-green-500"
                    style={{ transform: `rotate(${(analytics?.overview?.avgEngagementRate || 0) * 3.6}deg)` }}
                  />
                </div>
                <p className="text-purple-300/60 text-sm mt-4">Average engagement rate</p>
                <Badge className="mt-2 bg-green-500/20 text-green-300 border-green-500/30">
                  {analytics?.overview?.avgEngagementRate >= 5 ? 'Above Average' : 'Room to Grow'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Posts */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm mt-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-400" />
              Top Performing Posts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics?.topPosts?.map((post, index) => (
                <div 
                  key={post.id} 
                  className="flex items-center gap-4 p-4 bg-purple-500/10 rounded-lg"
                  data-testid={`top-post-${index}`}
                >
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white truncate">{post.caption}</p>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-1 text-blue-300">
                      <Eye className="h-4 w-4" />
                      {post.reach}
                    </div>
                    <div className="flex items-center gap-1 text-pink-300">
                      <Heart className="h-4 w-4" />
                      {post.likes}
                    </div>
                    <div className="flex items-center gap-1 text-purple-300">
                      <MessageCircle className="h-4 w-4" />
                      {post.comments}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Platform Breakdown */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm mt-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Share2 className="h-5 w-5 text-purple-400" />
              Platform Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {analytics?.platformBreakdown?.map((platform) => (
                <div 
                  key={platform.platform} 
                  className="p-4 bg-purple-500/10 rounded-lg"
                  data-testid={`platform-${platform.platform.toLowerCase()}`}
                >
                  <h4 className="text-white font-medium mb-3">{platform.platform}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-purple-300/60">Posts</span>
                      <span className="text-white">{platform.posts}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-300/60">Reach</span>
                      <span className="text-white">{platform.reach.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-300/60">Engagement</span>
                      <span className="text-white">{platform.engagement}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pro Features Teaser */}
        <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 backdrop-blur-sm mt-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-amber-500/30 p-3 rounded-xl">
                  <Zap className="h-6 w-6 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Unlock Advanced Analytics</h3>
                  <p className="text-amber-200/70 text-sm">
                    Get detailed insights, follower demographics, best posting times & more
                  </p>
                </div>
              </div>
              <Button
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400"
                onClick={() => navigate('/subscription')}
                data-testid="upgrade-analytics-button"
              >
                Upgrade to Pro
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DailyDropAnalytics;
